import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BRAND from '../config/brand';
import { API_BASE } from '../config';
import {
  EXPLORER_VIEWS,
  colorForAsset,
  formatReturnPct,
  formatUsdCompact,
} from '../config/investmentPerformanceProxies';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import { CALCULATOR_ANALYTICS_IDS } from '../lib/analytics';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import { CONTENT_CATEGORIES } from '../config/planningOffers';
import './InvestmentPerformanceExplorer.css';

const CALCULATOR_ID = 'investment_performance_explorer';
const CALCULATOR_NAME = CALCULATOR_ANALYTICS_IDS.investment_performance_explorer;

const INTRO_DISCLOSURE =
  'Educational information only. This explorer uses historical market data and benchmark proxies to illustrate how investments have performed. It does not provide individualized investment advice or determine whether an investment is appropriate for your goals, risk tolerance, tax situation, time horizon, or overall financial plan.';

const RESULTS_DISCLOSURE =
  'Historical and hypothetical results are not guarantees of future performance. Actual investment results may differ materially because of market conditions, fees, taxes, spreads, cash flows, trading activity, custody arrangements, data limitations, and investment timing. You may lose some or all of the amount invested.';

const STRATEGY_FIT_DISCLOSURE =
  'A high historical return or large market capitalization does not mean an investment is appropriate for you. An investment may conflict with your required return, liquidity needs, time horizon, tax strategy, withdrawal plan, risk capacity, or ability to tolerate losses.';

const PROXY_DISCLOSURE =
  'ETFs and market indexes are used as educational proxies for asset categories. Proxy performance is not the same as an investor’s actual account performance and does not represent a recommendation to purchase the proxy.';

const MONTHLY_AS_OF_DISCLOSURE =
  'Callan chart and trailing return figures are based on the previous completed calendar month (month-end prices). This page is not updated daily; data is refreshed about once a month and will lag live markets.';

const CRYPTO_DISCLOSURE =
  'Cryptocurrency is speculative and may experience extreme price changes, limited liquidity, cybersecurity incidents, custody losses, platform failures, fraud, technology risks, and regulatory changes. Crypto assets may not provide the same protections associated with traditional securities accounts. You could lose your entire investment.';

const CRYPTO_DATA_DISCLOSURE =
  'Cryptocurrency rankings and values can change continuously. Market capitalization is generally based on reported price multiplied by reported circulating supply. Prices, supply figures, rankings, and returns may differ across data providers and trading venues.';

function buildQuiltModel(assets, calendarReturns) {
  const byAssetYear = new Map();
  for (const row of calendarReturns || []) {
    byAssetYear.set(`${row.asset_id}:${row.calendar_year}`, row);
  }

  const years = [...new Set((calendarReturns || []).map((r) => r.calendar_year))]
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  // Prefer a readable window (last 10 completed + partial years)
  const displayYears = years.slice(-11);

  const columns = displayYears.map((year) => {
    const cells = (assets || [])
      .map((asset) => {
        const row = byAssetYear.get(`${asset.id}:${year}`);
        return {
          asset,
          year,
          return_pct: row ? row.return_pct : null,
          is_partial_year: row ? Boolean(row.is_partial_year) : false,
        };
      })
      .filter((c) => c.return_pct !== null && c.return_pct !== undefined)
      .sort((a, b) => b.return_pct - a.return_pct);

    return { year, cells };
  });

  return { displayYears, columns };
}

function contrastText(hex) {
  if (!hex || hex.length < 7) return '#111';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#1a2332' : '#fff';
}

/** Format API YYYY-MM-DD as a month-end as-of label for the Callan / trailing panels. */
function formatMonthEndAsOf(isoDate) {
  if (!isoDate) return '';
  const parts = String(isoDate).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    return `Month-end data as of ${isoDate}`;
  }
  const [y, m, d] = parts;
  const label = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `Month-end data as of ${label} (previous completed month)`;
}

export default function InvestmentPerformanceExplorer() {
  const [activeViewId, setActiveViewId] = useState('asset_classes');
  const [includeStablecoins, setIncludeStablecoins] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disclosuresOpen, setDisclosuresOpen] = useState(false);

  const activeView = EXPLORER_VIEWS.find((v) => v.id === activeViewId) || EXPLORER_VIEWS[0];
  const isCrypto = activeView.kind === 'crypto';

  const { trackResultView, trackComplete, trackFieldInteraction } = useCalculatorAnalytics({
    calculatorId: CALCULATOR_ID,
    calculatorName: CALCULATOR_NAME,
  });

  useEffect(() => {
    if (!activeView.enabled || !activeView.endpoint) {
      setPayload(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url =
          activeView.kind === 'crypto'
            ? `${API_BASE}${activeView.endpoint}?include_stablecoins=${includeStablecoins ? '1' : '0'}`
            : `${API_BASE}${activeView.endpoint}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        setPayload(data);
        trackFieldInteraction(`view_${activeView.id}`);
        trackResultView({ result_type: `${activeView.id}_returns` });
        trackComplete({ result_type: `${activeView.id}_returns` });
      } catch (err) {
        if (cancelled) return;
        setError('Market data is temporarily unavailable. Please try again later.');
        setPayload(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeView, includeStablecoins, trackComplete, trackFieldInteraction, trackResultView]);

  const assets = useMemo(() => payload?.data?.assets || [], [payload]);
  const calendarReturns = useMemo(() => payload?.data?.calendar_returns || [], [payload]);
  const trailingReturns = useMemo(() => payload?.data?.trailing_returns || [], [payload]);
  const cryptoAssets = useMemo(
    () => (isCrypto ? payload?.data?.assets || [] : []),
    [isCrypto, payload]
  );

  const quilt = useMemo(
    () => buildQuiltModel(assets, calendarReturns),
    [assets, calendarReturns]
  );

  const trailingByAssetId = useMemo(() => {
    const map = new Map();
    for (const row of trailingReturns) map.set(row.asset_id, row);
    return map;
  }, [trailingReturns]);

  const asOfLabel = !payload?.data_as_of
    ? loading
      ? ''
      : 'As-of date unavailable'
    : isCrypto
      ? `As of ${payload.data_as_of}`
      : formatMonthEndAsOf(payload.data_as_of);

  return (
    <div className="ipe-page">
      <header className="ipe-header">
        <div className="ipe-header-inner">
          <Link to="/links" className="ipe-logo-link" aria-label="Bautista Planning and Analytics">
            <img src={BRAND.logoSrc} alt="" className="ipe-logo-img" />
          </Link>
        </div>
      </header>

      <div className="ipe-body">
        <div className="ipe-inner">
          <div className="ipe-title-block">
            <p className="ipe-eyebrow">Educational market explorer</p>
            <h1>Investment Performance Explorer</h1>
            <p className="ipe-lede">
              See what performed across major asset categories, understand the risk behind the
              return, and consider whether your portfolio works with your financial plan.
            </p>
            <p className="ipe-disclosure ipe-disclosure--intro" role="note">
              {INTRO_DISCLOSURE}
            </p>
          </div>

          <nav className="ipe-tabs" aria-label="Market views" role="tablist">
            {EXPLORER_VIEWS.map((view) => {
              const isActive = view.id === activeViewId;
              return (
                <button
                  key={view.id}
                  type="button"
                  role="tab"
                  className={`ipe-tab${isActive ? ' ipe-tab--active' : ''}${
                    !view.enabled ? ' ipe-tab--soon' : ''
                  }`}
                  aria-selected={isActive}
                  aria-disabled={!view.enabled}
                  disabled={!view.enabled}
                  title={view.enabled ? undefined : 'Coming soon'}
                  onClick={() => {
                    if (view.enabled) setActiveViewId(view.id);
                  }}
                >
                  {view.label}
                  {!view.enabled ? <span className="ipe-tab-soon">Soon</span> : null}
                </button>
              );
            })}
          </nav>

          {payload?.is_stale ? (
            <p className="ipe-banner ipe-banner--stale" role="status">
              Showing the latest validated month-end dataset on file. The monthly refresh may be
              overdue — figures intentionally lag live markets until the next update.
            </p>
          ) : null}

          {loading ? <p className="ipe-status">Loading market data…</p> : null}
          {error ? <p className="ipe-status ipe-status--error">{error}</p> : null}

          {!loading && !error && (isCrypto ? cryptoAssets.length === 0 : assets.length === 0) ? (
            <p className="ipe-status">
              {isCrypto
                ? 'Cryptocurrency market data has not been published yet. Run the CoinGecko refresh, then reload.'
                : 'Market data has not been published yet. Check back after the next scheduled refresh.'}
            </p>
          ) : null}

          {!loading && !error && isCrypto && cryptoAssets.length > 0 ? (
            <>
              <section className="ipe-panel" aria-labelledby="ipe-crypto-heading">
                <div className="ipe-panel-head">
                  <h2 id="ipe-crypto-heading">Cryptocurrency Market Explorer</h2>
                  <p className="ipe-asof">{asOfLabel}</p>
                </div>
                <p className="ipe-panel-note">
                  Explore the largest cryptocurrencies by market capitalization. This information is
                  educational and does not indicate that any cryptocurrency is appropriate for a
                  visitor’s portfolio.
                </p>
                <p className="ipe-disclosure" role="note">
                  {CRYPTO_DISCLOSURE}
                </p>

                <div className="ipe-crypto-filter" role="group" aria-label="Stablecoin filter">
                  <button
                    type="button"
                    className={`ipe-seg${!includeStablecoins ? ' ipe-seg--active' : ''}`}
                    onClick={() => setIncludeStablecoins(false)}
                  >
                    Exclude stablecoins
                  </button>
                  <button
                    type="button"
                    className={`ipe-seg${includeStablecoins ? ' ipe-seg--active' : ''}`}
                    onClick={() => setIncludeStablecoins(true)}
                  >
                    Include stablecoins
                  </button>
                </div>

                <div className="ipe-table-scroll">
                  <table className="ipe-table ipe-table--crypto">
                    <thead>
                      <tr>
                        <th scope="col">Rank</th>
                        <th scope="col">Name</th>
                        <th scope="col">Price</th>
                        <th scope="col">Market cap</th>
                        <th scope="col">Share</th>
                        <th scope="col">24h</th>
                        <th scope="col">7d</th>
                        <th scope="col">30d</th>
                        <th scope="col">YTD</th>
                        <th scope="col">1Y</th>
                        <th scope="col">Vol. ann.</th>
                        <th scope="col">Max DD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cryptoAssets.map((asset) => (
                        <tr key={asset.id}>
                          <td>{asset.rank}</td>
                          <th scope="row">
                            {asset.name}
                            <span className="ipe-ticker">{asset.symbol}</span>
                          </th>
                          <td>{formatUsdCompact(asset.price_usd)}</td>
                          <td>{formatUsdCompact(asset.market_cap_usd)}</td>
                          <td>{formatReturnPct(asset.market_cap_share)}</td>
                          <td>{formatReturnPct(asset.return_24h)}</td>
                          <td>{formatReturnPct(asset.return_7d)}</td>
                          <td>{formatReturnPct(asset.return_30d)}</td>
                          <td>{formatReturnPct(asset.return_ytd)}</td>
                          <td>{formatReturnPct(asset.return_1y)}</td>
                          <td>{formatReturnPct(asset.volatility_ann)}</td>
                          <td>{formatReturnPct(asset.max_drawdown)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="ipe-crypto-bars" aria-label="Market-cap share chart">
                  {cryptoAssets.map((asset) => {
                    const share = Number(asset.market_cap_share) || 0;
                    return (
                      <div key={`bar-${asset.id}`} className="ipe-crypto-bar-row">
                        <span className="ipe-crypto-bar-label">
                          {asset.rank}. {asset.symbol}
                        </span>
                        <div className="ipe-crypto-bar-track">
                          <div
                            className="ipe-crypto-bar-fill"
                            style={{ width: `${Math.max(2, share * 100)}%` }}
                          />
                        </div>
                        <span className="ipe-crypto-bar-value">{formatUsdCompact(asset.market_cap_usd)}</span>
                      </div>
                    );
                  })}
                </div>

                <p className="ipe-disclosure" role="note">
                  {CRYPTO_DATA_DISCLOSURE}
                </p>
                <p className="ipe-disclosure" role="note">
                  {RESULTS_DISCLOSURE}
                </p>
                <p className="ipe-disclosure" role="note">
                  {STRATEGY_FIT_DISCLOSURE}
                </p>
              </section>

              <details
                className="ipe-methodology"
                open={disclosuresOpen}
                onToggle={(e) => setDisclosuresOpen(e.target.open)}
              >
                <summary>Methodology, risks, and important disclosures</summary>
                <div className="ipe-methodology-body">
                  <p>{INTRO_DISCLOSURE}</p>
                  <p>{CRYPTO_DISCLOSURE}</p>
                  <p>{CRYPTO_DATA_DISCLOSURE}</p>
                  <p>{RESULTS_DISCLOSURE}</p>
                  <p>{STRATEGY_FIT_DISCLOSURE}</p>
                  <p>
                    Rankings are selected dynamically from CoinGecko market-cap order at the latest
                    validated snapshot. Default comparisons exclude stablecoins; use the filter to
                    include them. Market capitalization is not a measure of safety or suitability.
                    Methodology version {payload?.methodology_version || '1'}. Source:{' '}
                    {payload?.source_label}.
                  </p>
                </div>
              </details>

              <CalculatorDiscoveryCta
                calculatorId={CALCULATOR_ID}
                calculatorName={CALCULATOR_NAME}
                headline="Want help connecting this to your plan?"
                body={[
                  'Crypto exposure is only one input. A comprehensive financial plan can help you interpret diversification, risk, taxes, and whether your portfolio supports your goals.',
                ]}
              />
              <ServiceNextStep
                contentCategory={CONTENT_CATEGORIES.INVESTMENT}
                calculatorId={CALCULATOR_ID}
                calculatorName={CALCULATOR_NAME}
                promotionalAngle="investment_explorer"
              />
            </>
          ) : null}

          {!loading && !error && !isCrypto && assets.length > 0 ? (
            <>
              <section className="ipe-panel" aria-labelledby="ipe-quilt-heading">
                <div className="ipe-panel-head">
                  <h2 id="ipe-quilt-heading">{activeView.quiltTitle}</h2>
                  <p className="ipe-asof">{asOfLabel}</p>
                </div>
                <p className="ipe-panel-note">
                  Each column ranks proxies from highest to lowest return for that calendar year.
                  Colors identify the category — not a heat map of performance. Scroll horizontally
                  to see additional years.
                </p>
                <p className="ipe-disclosure" role="note">
                  {MONTHLY_AS_OF_DISCLOSURE}
                </p>

                <div className="ipe-quilt-scroll">
                  <div
                    className="ipe-quilt"
                    role="table"
                    aria-label={`${activeView.quiltTitle} quilt`}
                  >
                    <div className="ipe-quilt-row ipe-quilt-row--head" role="row">
                      {quilt.columns.map((col) => (
                        <div key={col.year} className="ipe-quilt-year" role="columnheader">
                          {col.year}
                        </div>
                      ))}
                    </div>
                    {Array.from({
                      length: Math.max(0, ...quilt.columns.map((c) => c.cells.length)),
                    }).map((_, rankIdx) => (
                      <div key={`rank-${rankIdx}`} className="ipe-quilt-row" role="row">
                        {quilt.columns.map((col) => {
                          const cell = col.cells[rankIdx];
                          if (!cell) {
                            return (
                              <div
                                key={`${col.year}-empty-${rankIdx}`}
                                className="ipe-quilt-cell ipe-quilt-cell--empty"
                                role="cell"
                              />
                            );
                          }
                          const bg = colorForAsset(cell.asset);
                          return (
                            <div
                              key={`${col.year}-${cell.asset.id}`}
                              className="ipe-quilt-cell"
                              role="cell"
                              style={{ background: bg, color: contrastText(bg) }}
                              title={`${cell.asset.name} (${cell.asset.ticker})`}
                            >
                              <span className="ipe-quilt-label">{cell.asset.short_name}</span>
                              <span className="ipe-quilt-ret">
                                {formatReturnPct(cell.return_pct)}
                                {cell.is_partial_year ? '*' : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="ipe-footnote">
                  * Partial year through the previous completed month (month-end), not through today.
                </p>
                <p className="ipe-disclosure" role="note">
                  {PROXY_DISCLOSURE}
                </p>
              </section>

              <section className="ipe-panel" aria-labelledby="ipe-trailing-heading">
                <div className="ipe-panel-head">
                  <h2 id="ipe-trailing-heading">{activeView.trailingTitle}</h2>
                  <p className="ipe-asof">{asOfLabel}</p>
                </div>
                <p className="ipe-panel-note">
                  Multi-year figures are annualized through the previous completed month. Blank
                  cells mean insufficient history — never treated as zero.
                </p>

                <div className="ipe-table-scroll">
                  <table className="ipe-table">
                    <thead>
                      <tr>
                        <th scope="col">{activeView.nameColumn}</th>
                        <th scope="col">1M</th>
                        <th scope="col">3M</th>
                        <th scope="col">YTD</th>
                        <th scope="col">1Y</th>
                        <th scope="col">3Y ann.</th>
                        <th scope="col">5Y ann.</th>
                        <th scope="col">10Y ann.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((asset) => {
                        const row = trailingByAssetId.get(asset.id);
                        return (
                          <tr key={asset.id}>
                            <th scope="row">
                              <span
                                className="ipe-swatch"
                                style={{ background: colorForAsset(asset) }}
                                aria-hidden
                              />
                              {asset.name}
                              <span className="ipe-ticker">{asset.ticker}</span>
                            </th>
                            <td>{formatReturnPct(row?.return_1m)}</td>
                            <td>{formatReturnPct(row?.return_3m)}</td>
                            <td>{formatReturnPct(row?.return_ytd)}</td>
                            <td>{formatReturnPct(row?.return_1y)}</td>
                            <td>{formatReturnPct(row?.return_3y_annualized)}</td>
                            <td>{formatReturnPct(row?.return_5y_annualized)}</td>
                            <td>{formatReturnPct(row?.return_10y_annualized)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <p className="ipe-disclosure" role="note">
                  {RESULTS_DISCLOSURE}
                </p>
                <p className="ipe-disclosure" role="note">
                  {STRATEGY_FIT_DISCLOSURE}
                </p>
              </section>

              <details
                className="ipe-methodology"
                open={disclosuresOpen}
                onToggle={(e) => setDisclosuresOpen(e.target.open)}
              >
                <summary>Methodology, risks, and important disclosures</summary>
                <div className="ipe-methodology-body">
                  {/* COMPLIANCE: Final disclosure wording requires owner / compliance review before production. Not legal advice. */}
                  <p>{INTRO_DISCLOSURE}</p>
                  <p>{MONTHLY_AS_OF_DISCLOSURE}</p>
                  <p>{RESULTS_DISCLOSURE}</p>
                  <p>{STRATEGY_FIT_DISCLOSURE}</p>
                  <p>{PROXY_DISCLOSURE}</p>
                  <p>
                    Returns are derived from month-end adjusted closing prices for ETF proxies and
                    are refreshed about once a month after the prior month closes. Adjusted prices
                    account for splits and distributions where the data provider supplies them.
                    Calculations exclude taxes, advisory fees, trading costs, and investor cash
                    flows. Methodology version {payload?.methodology_version || '1'}. Source:{' '}
                    {payload?.source_label}.
                  </p>
                  {payload?.unavailable_assets?.length ? (
                    <p>
                      Unavailable for this refresh:{' '}
                      {payload.unavailable_assets.map((a) => a.ticker).join(', ')}.
                    </p>
                  ) : null}
                </div>
              </details>

              <CalculatorDiscoveryCta
                calculatorId={CALCULATOR_ID}
                calculatorName={CALCULATOR_NAME}
                headline="Want help connecting this to your plan?"
                body={[
                  'Past category returns are only one input. A comprehensive financial plan can help you interpret diversification, risk, taxes, and whether your portfolio supports your goals.',
                ]}
              />
              <ServiceNextStep
                contentCategory={CONTENT_CATEGORIES.INVESTMENT}
                calculatorId={CALCULATOR_ID}
                calculatorName={CALCULATOR_NAME}
                promotionalAngle="investment_explorer"
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
